<template>
    <div class="w-full px-12 mt-48 xs:w-1/2 sm:w-1/3">
        <!--     CARD START-->
        <div>
            <div class="p-20 bg-white rounded-md">
                <div class="flex items-center">
                    <div class="flex items-center justify-center flex-shrink-0 w-48 h-48 bg-yellow-200 rounded-full">
                        <div class="flex-shrink-0 block w-24 h-24 fill-current text-primary">
                            <IconStarSVG class="w-24 h-24" />
                        </div>
                    </div>

                    <span class="ml-12 font-normal title-lg text-primary">{{ card.card_type }}</span>
                </div>

                <span class="mt-16 self-label">{{ $t('dashboard.bonus_card.card_no') }}</span>
                <span class="text-base font-semibold sm:text-md">{{ card.card_number }}</span>

                <span class="mt-16 self-label">{{ $t('info.valid_until') }}</span>
                <span
                    v-if="!card.card_valid_until || new Date(card.card_valid_until).getFullYear() - new Date().getFullYear() > 10"
                    class="text-base font-semibold sm:text-md"
                    >{{ $t('info.indefinite_end_date') }}</span
                >
                <span v-else-if="card.card_valid_until" class="text-base font-semibold sm:text-md">{{
                    formatDate(card.card_valid_until)
                }}</span>
                <button @click="initComponent(cardId)" :data-open-modal="'bonuscard-' + card.bonus_card_id" class="mt-16 self-btn-plain">
                    <span class="w-16 h-16 ml-0 mr-4 self-btn-plain__icon">
                        <IconPencilSVG />
                    </span>
                    <span class="self-btn-plain__text">{{ $t('info.modify') }}</span>
                </button>
            </div>
        </div>
        <!--    CARD END-->

        <!--     MODAL START -->
        <div class="micromodal" :id="'bonuscard-' + card.bonus_card_id" aria-hidden="true">
            <div class="px-10 micromodal__overlay" tabindex="-1">
                <div class="micromodal__container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                    <header class="micromodal__header">
                        <h2 class="pb-24 pr-48 title-2xl text-primary">{{ $t('info.modify') }}</h2>

                        <button class="micromodal__close" aria-label="Close modal" data-micromodal-close>
                            <span class="flex-shrink-0 block w-20 h-20 text-black pointer-events-none fill-current">
                                <IconXSVG />
                            </span>
                        </button>
                    </header>
                    <div class="micromodal__content">
                        <div class="flex self-form-wrap">
                            <div :class="['w-full self-form-wrap-item', { 'e5-error': cardId.length === 0 }]">
                                <label class="e5-form-label" for="id"
                                    >{{ $t('info.card_id') }}<span class="e5-form-field-required">*</span></label
                                >
                                <div class="relative">
                                    <input type="text" id="id" placeholder="" name="id" class="e5-input" v-model="cardId" disabled />
                                </div>
                            </div>
                            <div :class="['w-full self-form-wrap-item', { 'e5-error': cardName.length === 0 }]">
                                <label class="e5-form-label" for="name"
                                    >{{ $t('info.card_name') }}<span class="e5-form-field-required">*</span></label
                                >
                                <div class="relative">
                                    <input type="text" id="name" placeholder="" name="name" class="e5-input" v-model="cardName" />
                                </div>
                            </div>
                            <div :class="['w-full self-form-wrap-item', { 'e5-error': cardNo.length === 0 }]">
                                <label class="e5-form-label" for="number"
                                    >{{ $t('dashboard.bonus_card.card_no') }}<span class="e5-form-field-required">*</span></label
                                >
                                <div class="relative">
                                    <input type="text" id="number" placeholder="" name="number" class="e5-input" v-model="cardNo" />
                                </div>
                            </div>
                            <Dob
                                :presetDate="
                                    new Date(cardValidUntil).toLocaleDateString('et-EE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric'
                                    })
                                "
                                :label="$t('info.valid_until')"
                                @update:date="updatevalidUntilDate"
                                :disable="isIndefinite.length === 1"
                                has-past-and-future-dates
                            />
                            <div class="w-full self-form-wrap-item">
                                <input
                                    class="e5-checkbox"
                                    type="checkbox"
                                    :id="'bonuscardIndefinite' + cardId"
                                    :name="'bonuscardIndefinite' + cardId"
                                    value="2999-12-31"
                                    v-model="isIndefinite"
                                />
                                <label class="" :for="'bonuscardIndefinite' + cardId">
                                    {{ $t('info.indefinite_end_date') }}
                                </label>
                            </div>
                            <div :class="['w-full self-form-wrap-item', { 'e5-error-choices': !selectedToplevel }]">
                                <label class="e5-form-label" for="toplevel"
                                    >{{ $t('reports.form.service_groups') }}<span class="e5-form-field-required">*</span></label
                                >
                                <div class="relative">
                                    <div class="self-form-select-wrap">
                                        <select name="toplevel" id="toplevel" multiple :class="'self-multiselect-' + cardId"></select>
                                    </div>
                                </div>
                            </div>
                            <div class="flex items-center justify-between px-8 pt-16 w-full">
                                <div>
                                    <button @click="saveBonuscard" data-micromodal-close class="self-btn-primary" :disabled="!isFormOk">
                                        {{ $t('info.save') }}
                                        <span class="text-yellow-300 self-btn-primary__icon">
                                            <IconArrowAltSVG />
                                        </span>
                                    </button>
                                    <button
                                        data-micromodal-close
                                        :data-open-modal="'bonuscard-delete-confirm-' + card.card_number"
                                        class="self-btn-primary-alert mt-10"
                                    >
                                        {{ $t('info.delete_bonuscard') }}
                                        <span class="text-yellow-300 self-btn-primary__icon">
                                            <IconXSVG />
                                        </span>
                                    </button>
                                </div>
                                <div>
                                    <button data-micromodal-close class="self-btn-plain">
                                        <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                                            <IconXSVG />
                                        </span>
                                        <span class="self-btn-plain__text">{{ $t('info.cancel') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--    MODAL END-->
        <!--    CONFIRMATION MODAL START -->
        <div class="micromodal" :id="'bonuscard-delete-confirm-' + card.card_number" aria-hidden="true">
            <div class="px-10 micromodal__overlay" tabindex="-1">
                <div class="micromodal__container" role="dialog" aria-modal="true" aria-labelledby="modal-title">
                    <header class="micromodal__header">
                        <h2 class="pb-24 pr-48 title-2xl text-primary">
                            {{ $t('info.delete_bonuscard_confirmation') }}
                        </h2>
                        <button data-micromodal-close class="micromodal__close" aria-label="Close modal">
                            <span class="flex-shrink-0 block w-20 h-20 text-black pointer-events-none fill-current">
                                <IconXSVG />
                            </span>
                        </button>
                    </header>
                    <div class="micromodal__content">
                        <div class="flex self-form-wrap">
                            <div class="flex items-center justify-between w-full px-8 pt-16">
                                <div>
                                    <button @click="deleteBonuscard" class="self-btn-primary">
                                        {{ $t('info.yes') }}
                                    </button>
                                </div>

                                <div>
                                    <button data-micromodal-close class="self-btn-plain">
                                        <span class="w-16 ml-0 mr-4 self-btn-plain__icon">
                                            <IconXSVG />
                                        </span>
                                        <span class="self-btn-plain__text">{{ $t('info.cancel') }}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!--   CONFIRMATION MODAL END-->
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import IconNotesSVG from '@/assets/static/icons-src/icon-notes.svg';
import IconPencilSVG from '@/assets/static/icons-src/icon-pencil.svg';
import IconStarSVG from '@/assets/static/icons-src/icon-star.svg';
import IconXSVG from '@/assets/static/icons-src/icon-x.svg';
import IconArrowAltSVG from '@/assets/static/icons-src/icon-arrow-alt.svg';
import Dob from '@/components/info-view-components/Dob.vue';
import UtilsMixin from '@/scripts/utils';
import { IBonusCard } from '@/domain/IBonusCard';
import { IDeleteBonuscardRequest, ISaveBonuscardRequest } from '@/domain/ISaveInfo';
import store from '@/store';
import MicroModal from 'micromodal';
import { IToplevel } from '@/domain/IToplevel';
import Choices from 'choices.js';
import $ from 'jquery';

@Component({
    components: {
        IconNotesSVG,
        IconPencilSVG,
        IconStarSVG,
        IconXSVG,
        IconArrowAltSVG,
        Dob
    },
    mixins: [UtilsMixin]
})
export default class BonusCard extends Vue {
    @Prop(Object) card!: IBonusCard;

    cardId = '';
    cardName = '';
    cardNo = '';
    cardValidUntil = '';
    cardToplevel = '';
    selectedToplevel = '';
    choices = null;
    isIndefinite = [];

    get isFormOk() {
        return (
            this.cardId !== '' &&
            this.cardName !== '' &&
            this.cardNo !== '' &&
            (this.cardValidUntil !== '' || this.isIndefinite.length > 0) &&
            this.selectedToplevel !== '' &&
            this.selectedToplevel !== undefined
        );
    }

    private async initMultiSelects(cardId: string) {
        if (this.choices) {
            this.choices.destroy();
        }
        const selectElement = document.querySelector(`.self-multiselect-${cardId}`) as HTMLSelectElement;
        if (selectElement === null) return;
        const placeholderText = this.$t('reports.form.choose') as string;
        this.choices = new Choices(document.querySelector(`.self-multiselect-${cardId}`), {
            removeItemButton: true,
            itemSelectText: placeholderText,
            maxItemCount: 2,
            maxItemText: this.$t('reports.form.only_one_can_be_selected') as string,
            noChoicesText: this.$t('reports.form.no_choices_to_choose_from') as string,
            allowHTML: true
        });

        this.choices.passedElement.element.addEventListener(
            'change',
            (event) => {
                const selectedValues = $(event.target).val() as string[];
                if (selectedValues.length > 1) {
                    // Convert the HTMLSelectElement to an array-like object
                    const options = Array.from(selectElement.options);

                    // Find the option with the specified value and remove it
                    const optionToRemove = options.find((option) => option.value === selectedValues[0]);
                    if (optionToRemove) {
                        selectElement.removeChild(optionToRemove);
                    }

                    // Update Choices.js instance to reflect the changes
                    this.choices.removeActiveItemsByValue(selectedValues[0]);
                    selectedValues.splice(0, 1);
                    this.choices.setChoiceByValue(selectedValues);
                }
                this.selectedToplevel = selectedValues[0];
            },
            false
        );

        const toplevels: IToplevel[] = store.state.toplevel;
        // Filter out unwanted toplevels
        const filteredToplevels = toplevels?.filter((toplevel) => ['Air', 'Hotel', 'Car', 'Ferry', 'Other'].includes(toplevel.toplevel_id));

        this.choices.setChoices(
            filteredToplevels?.map((toplevel) => ({
                value: toplevel.toplevel_id,
                label: toplevel.toplevel_name,
                selected: toplevel.toplevel_id === this.cardToplevel
            })),
            'value',
            'label',
            false
        );
        this.selectedToplevel = this.cardToplevel;
    }

    initComponent(cardId: string) {
        this.initializeCardProperties();
        this.initMultiSelects(cardId);
    }

    private initializeCardProperties(): void {
        this.cardId = this.card.bonus_card_id !== undefined ? this.card.bonus_card_id : '';
        this.cardName = this.card.card_type !== undefined ? this.card.card_type : '';
        this.cardNo = this.card.card_number !== undefined ? this.card.card_number : '';
        this.cardValidUntil = this.card.card_valid_until !== undefined ? this.card.card_valid_until : '';
        this.cardToplevel = this.card.toplevel !== undefined ? this.card.toplevel : '';
        if (new Date(this.card.card_valid_until).getFullYear() - new Date().getFullYear() > 10) {
            this.isIndefinite = ['2999-12-31'];
        } else {
            this.isIndefinite = [];
        }
    }

    updatevalidUntilDate(date: string) {
        this.cardValidUntil = date;
    }

    async saveBonuscard() {
        const request: ISaveBonuscardRequest = {
            customerId: store.getters.getCustomerInfo.customerid,
            bonuscardId: this.cardId,
            bonuscardName: this.cardName,
            bonuscardNumber: this.cardNo,
            bonuscardValidUntil: this.isIndefinite.length === 0 ? this.cardValidUntil : this.isIndefinite[0],
            toplevel: this.selectedToplevel
        };
        const success = await store.dispatch('saveBonuscard', request);
        if (!success) {
            this.requestErrorFunction();
            return;
        }
        await store.dispatch('updateCustomerInfo');
    }

    async deleteBonuscard() {
        const request: IDeleteBonuscardRequest = {
            customerId: store.getters.getCustomerInfo.customerid,
            bonuscardId: this.card.bonus_card_id
        };
        const success = await store.dispatch('deleteBonuscard', request);
        if (!success) {
            this.requestErrorFunction();
            MicroModal.close('bonuscard-delete-confirm-' + this.card.card_number);
            return;
        }
        MicroModal.close('bonuscard-delete-confirm-' + this.card.card_number);
        await store.dispatch('updateCustomerInfo');
    }

    private requestErrorFunction() {
        Vue.notify({
            group: 'api',
            title: 'Server error',
            text: 'Error while while processing bonuscard request',
            type: 'warn'
        });
    }

    @Watch('card', { immediate: true, deep: true })
    onCardChanged(): void {
        this.initializeCardProperties();
    }
}
</script>

<style scoped></style>
